/// <amd-module name="typeahead/core/abTest" />
/// <amd-dependency path="jquery" name="$"/>
/// <amd-dependency path="uitk" name="uitk"/>

/// <reference path="../../typings/typeahead/uitk.d.ts" />
/// <reference path="../../typings/typeahead/dctk.d.ts" />
/// <reference path="../../typings/typeahead/abTest.d.ts" />

"use strict";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {DefaultPluginAssembly} from "typeahead/core/plugins/DefaultPluginAssembly";
import {SiteParam, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export interface IDctkParamsForUta {
    readonly guid: string;
    readonly tpid: number;
    readonly eapid: number;
    readonly name: string;
    readonly logToOmniture: boolean;
    readonly overrides: string;
    readonly callback: IDctkCallback;
}

interface IAbacusParams {
    readonly guid: string;
    readonly tpid: number;
    readonly eapid: number;
    readonly verbose: boolean;
    readonly id?: string[];
    readonly name?: string[];
    readonly overrides: string;
}

interface IAbacusResponse {
    evaluatedExperiments: IEvaluatedExperiment[];
}

interface IEvaluatedExperiment {
    instanceId: number;
    id: number;
    name: string;
    value: number;
    message: {
        description: string;
        id: number;
        type: string;
    };
}

export interface IErrorCode {
    readonly id: number;
    readonly severity: Severity;
    readonly problemOrigin: ProblemOrigin;
}

let persistentCookie: IPersistentCookie =
    uitk.createBrowserStorage ? uitk.createBrowserStorage("persistentCookies") : undefined;
let abovCookieValue: string = TypeaheadUtils.getCookie("abov");
let abTestMap: Object = <string & UtaExperiment> {};
let loggedTests: string [] = [];
// for same test, we don't want to fire multiple ajax requests
// instead we should have this cache map to store deferred object
// and resolve/reject the result
// subsequent call will be handled by abTestMap cache
let ajaxCallCache: Object = <string & JQueryDeferred<UtaExperiment>> {};

/*
    To test on localhost, install Charles and add a mapping:
    https://ewegithub.sb.karmalab.net/EWE/datacapture-toolkit/
        blob/master/datacapture/src/main/resources/datacapture/js/docs/testing-changes-with-charles.md
    from http://localhost:8080/api/bucketing/v1/evaluateExperiments to
        https://www.expedia.com/api/bucketing/v1/evaluateExperiments
    from http://localhost:8080/api/bucketing/v1/evaluateExperimentsAndLog to
        https://www.expedia.com/api/bucketing/v1/evaluateExperimentsAndLog

    Gets the variant of an Abacus Test from its name.
    Asynchronous method!
    @param expName: Mandatory - String - The experiment name to evaluate.
    @param successCallback: Mandatory - DoneCallback -
        The callback function called after receiving a successful response from abacus service.
    @param successCallback: Optional - errorCallback -
        The callback function called after receiving an errorful response from abacus service.
 */
function get(expName: string): JQueryPromise<UtaExperiment> {
    let deferred = $.Deferred();
    if (expName) {
        let test: UtaExperiment;
        let testCached = false;
        if (!!abTestMap[expName]) {
            test = abTestMap[expName];
            testCached = true;
        }

        if (!testCached) {
            if (ajaxCallCache.hasOwnProperty(expName)
                && typeof ajaxCallCache[expName] !== "undefined") {
                // it is being evaluated, we can just queue it
                ajaxCallCache[expName].push(deferred);
            } else {
                // first time trying to evaluate this test
                // we just cache it first
                ajaxCallCache[expName] = [deferred];
                require(["dctk/dctk"], (dctk) => {
                    dctk.evaluateExperiment(getDctkParams(expName, false, (experiment) => {
                        if (experiment.error) {
                            test = new UtaExperiment(expName, undefined, undefined);
                            abTestMap[test.name] = test;
                            publishTestResult(new UtaExperiment(expName), !processDctkError(experiment, expName));
                        } else {
                            if (experiment.id) {
                                test = new UtaExperiment(expName, experiment.id, experiment.value);
                                abTestMap[test.name] = test;
                            }
                            publishTestResult(test, true);
                        }
                    }));
                });
            }
        } else {
            deferred.resolve(test);
        }
    } else {
        deferred.reject(new UtaExperiment("experiment name must be provided"));
    }
    return deferred.promise();
}

/*
    Gets and Logs the variant of an Abacus Test from its name.
 */
export function getAndLog(expName: string): JQueryPromise <UtaExperiment> {
    if (expName) {
        // Do not log if test is already logged or previous test evaluation was an error
        if (loggedTests.indexOf(expName) < 0 && !(abTestMap[expName] && abTestMap[expName].value === undefined)) {
            loggedTests.push(expName);
            require(["dctk/dctk"], (dctk) => {
                dctk.evaluateExperimentAndLog(getDctkParams(expName, true, undefined));
            });
        }
        return get(expName);
    }
    TypeaheadErrorLogger.log(new Error("Experiment name missing when calling 'getAndLog'"),
        Severity.MustFix, ProblemOrigin.UniversalTypeahead);
    return $.Deferred().reject().promise();
}

export function evaluateAllExperiments(plugins: IPluginAssembly): void {
    evaluateExperiments(getAllExperimentNames(plugins.get(DefaultPluginAssembly.experimentIdentifier)));
}

function getAllExperimentNames(experimentalFeatures: IExperimentalFeature[]): string[] {
    let experimentNames: string[] = [];
    let getExperimentName = (experiment: UtaExperiment): void => {
        if (experimentNames.indexOf(experiment.name) < 0) {
            experimentNames.push(experiment.name);
        }
    };
    let getExperiments = (experimentalFeature: IExperimentalFeature): void => {
        let experiments: UtaExperiment[] = experimentalFeature.getExperiments();
        if (experiments && experiments.length) {
            experiments.forEach(getExperimentName);
        }
    };

    if (experimentalFeatures && experimentalFeatures.length) {
        experimentalFeatures.forEach(getExperiments);
    }
    return experimentNames;
}

function evaluateExperiments(experiments: string[]): void {
    if (experiments && experiments.length) {
        $.ajax({
            contentType: "application/json; charset=UTF-8",
            data: JSON.stringify(getAbacusParams(experiments)),
            dataType: "json",
            type: "POST",
            url: "/api/bucketing/v1/evaluateExperiments",
        }).done((response: IAbacusResponse) => {
            if (response && response.evaluatedExperiments && response.evaluatedExperiments.length) {
                response.evaluatedExperiments.forEach((exp: IEvaluatedExperiment) => {
                    abTestMap[exp.name] = new UtaExperiment(exp.name, exp.id, exp.message ? undefined : exp.value);
                });
            }
        }).fail(() => {
            TypeaheadErrorLogger.log(new Error("Unable to call abacus api to evaluate experiments at page load"),
                Severity.Excusable, ProblemOrigin.UniversalTypeahead);
        });
    }
}

/*
  Overrides the variant of an Abacus ID.

  @param testId: Mandatory - number - The experiment ID
  @param variant: Mandatory - number - the variant number (0=control, 1=variant1, etc)

*/
export function override(testId: number, variant: number): void {
    let overrideValue = [testId, "0", variant].join("|");
    let abov = persistentCookie.readItem("abov");
    let newAbov = overrideValue;
    if (abov) {
        newAbov = abov;
        let testIdStr = testId + "";
        let testIdIndex = newAbov.indexOf(testIdStr + "|");
        if (testIdIndex >= 0) {
            let patt = new RegExp(testIdStr + "\\|[0-9]+\\|[0-9]+");
            newAbov = newAbov.replace(patt, overrideValue);
        } else {
            newAbov += ":" + overrideValue;
        }
    }
    persistentCookie.saveItem("abov", newAbov);
}

/*
 Returns the content of the "abov" cookie, i.e. all the overriden experiments for the user on the current domain.
 */
export function getCookieValue(): string {
    return abovCookieValue ? abovCookieValue : undefined;
}

/*
 This function is mainly used for unit testing, don't use it anywhere else
*/
export function _mockExperimentWithBucket(expName: string, bucketVal: number): void {
    if (abTestMap.hasOwnProperty(expName)) {
        abTestMap[expName].value = bucketVal;
    } else {
        abTestMap[expName] = {
            id: -1,
            name: expName,
            value: bucketVal,
        };
    }
}

export class UtaExperiment {
    constructor(public name?: string, public id?: number, public value?: number) {}
}

// "Unexpected error occured"
let defaultErrorCode: IErrorCode = {
    id: 2000, problemOrigin: ProblemOrigin.DataCaptureToolkit, severity: Severity.Excusable};
let errorCodes: IErrorCode[] = [
    defaultErrorCode,
    // Evaluation failures
    // "Error occurred during getting preserve data."
    {id: 50000, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Invalid input parameters. {0}"
    {id: 50001, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Error occurred during bucketing."
    {id: 50002, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    /* Error occurred during the evaluation of one or more experiments   type: WARN
    This error could occur when the experiment is being evaluated for a site where it is not enabled.*/
    {id: 50003, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Negligible},
    // "The EntityType of the provided experiments must be the same. We have seen {0} and {1}"
    {id: 50004, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // Exposure Logging failures
    // "Request validation failed with: {0}"
    {id: 50100, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "An error occurred resolving experiment {0} ({1}) for exposure logging"
    {id: 50101, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Experiment {0} ({1}) is not in test mode and so cannot be logged"
    {id: 50102, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Experiment {0} ({1}) has already been logged"
    {id: 50103, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Could not find a treatment group for value {0} in experiment {1}"
    {id: 50104, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // Data reading Failures
    // "Can't read the EntityType of the experiments"
    {id: 70001, problemOrigin: ProblemOrigin.DataCaptureToolkit, severity: Severity.Excusable},
    // "Can't read the run type of the experiments"
    {id: 70002, problemOrigin: ProblemOrigin.DataCaptureToolkit, severity: Severity.Excusable},
    // Site hierarchy
    // "No site found for siteId={0}"*/
    {id: 8000, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "The given (TPID, EAPID) pair '(1, 0)' is not valid for the Experiment '13,081'"
    {id: 4301, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Negligible},

];

// Returns true if error is not ignoreable and false if error can be ignored
function processDctkError(response: any, expName: string, additionMsg?: string): boolean {
    let isError: boolean = true;
    if (response && response.error) {
        let errorCode: IErrorCode;
        let filter: IErrorCode[] = errorCodes.filter((errorCodeToFind: IErrorCode) => {
            let err = (typeof response.error === "string") ? response.error : response.error.toString();
            return err.indexOf(errorCodeToFind.id) > -1;
        });
        errorCode = (filter && filter.length > 0) ? filter[0] : defaultErrorCode;
        if (errorCode.severity !== Severity.Negligible) {
            TypeaheadErrorLogger.log(new Error("Error evaluating experiment [" + expName + "]: " + response.error +
                (additionMsg ? ", addition: " + additionMsg : "")), errorCode.severity, errorCode.problemOrigin);
        } else {
            isError = false;
        }
    }
    return isError;
}

function getDctkParams(expName: string, logToOmniture: boolean, callback: IDctkCallback): IDctkParamsForUta {
    let dctkParams: IDctkParamsForUta = {
        callback,
        eapid: TypeaheadUtils.getSiteParamValue(SiteParam.eapid),
        guid: TypeaheadUtils.getGuid(),
        logToOmniture,
        name: expName,
        overrides: getCookieValue(),
        tpid: TypeaheadUtils.getSiteParamValue(SiteParam.tpid),
    };
    return dctkParams;
}

function getAbacusParams(experiments: string[]): IAbacusParams {
    return {
        eapid: TypeaheadUtils.getSiteParamValue(SiteParam.eapid),
        guid: TypeaheadUtils.getGuid(),
        name: experiments,
        overrides: getCookieValue(),
        tpid: TypeaheadUtils.getSiteParamValue(SiteParam.tpid),
        verbose: true,
    };
}

function publishTestResult(experiment: UtaExperiment, success: boolean): void {
    let cacheKey = experiment ? experiment.name || "" : "";
    if (ajaxCallCache.hasOwnProperty(cacheKey)
        && typeof ajaxCallCache[cacheKey] !== "undefined") {
        while (ajaxCallCache[cacheKey].length) {
            // first come first served
            let deferred: JQueryDeferred<UtaExperiment> = ajaxCallCache[cacheKey].shift();
            // we notify the experiment: either a successful one or a failure one
            if (success) {
                deferred.resolve(experiment);
            } else {
                deferred.reject(experiment);
            }
        }
        ajaxCallCache[cacheKey] = undefined;
    }
}

export function getActiveClientTests(): string {
    let activeClientTests: string = "";
    for (let i: number = 0; i < loggedTests.length; i++) {
        if (abTestMap[loggedTests[i]] && abTestMap[loggedTests[i]].id) {
            activeClientTests += abTestMap[loggedTests[i]].id + "." + abTestMap[loggedTests[i]].value + "|";
        }
    }
    return activeClientTests ? activeClientTests.slice(0, -1) : activeClientTests;
}

/*  TODO: This function is to be used in unit tests only and needs to be removed
    when abTest module is refactored to a Class (TechDebt) */
export function resetCache(): void {
    abTestMap = {};
    loggedTests = [];
    ajaxCallCache = {};
}
